// src/features/account/useProfile.js
import { useCallback, useEffect, useRef, useState } from "react";
import Swal from "../../utils/apparifySwal";

import { userAPI } from "../../services/profileService";
import { pickProfileFromMe } from "./profileMapper";
import { clean, normalizePhone, normalizeBirthdate, validateField } from "./profileValidators";
import { FIELD_CONFIG } from "./profileConfig";
import { isSensitive } from "./masking";

export function useProfile({ swalBase, toast, onAuthExpired } = {}) {
  const [loading, setLoading] = useState(true);

  const [initial, setInitial] = useState(pickProfileFromMe(null));
  const [form, setForm] = useState(pickProfileFromMe(null));
  const [errors, setErrors] = useState({});

  // ✅ keep “one field editable at a time”
  const [editingKey, setEditingKey] = useState(null);

  const [savingField, setSavingField] = useState({});
  const [savedTick, setSavedTick] = useState({});

  // ✅ refs so the mount effect doesn't re-run when these change
  const swalBaseRef = useRef(swalBase);
  const toastRef = useRef(toast);
  const onAuthExpiredRef = useRef(onAuthExpired);

  useEffect(() => {
    swalBaseRef.current = swalBase;
  }, [swalBase]);

  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  useEffect(() => {
    onAuthExpiredRef.current = onAuthExpired;
  }, [onAuthExpired]);

  // ✅ LOAD PROFILE (run once on mount)
  useEffect(() => {
    let mounted = true;
    const ctrl = new AbortController();

    (async () => {
      try {
        setLoading(true);
        const me = await userAPI.me({ signal: ctrl.signal });

        if (!mounted) return;

        const p = pickProfileFromMe(me);
        p.phone = normalizePhone(p.phone);
        p.birthdate = normalizeBirthdate(p.birthdate);
        if (p.gender) p.gender = String(p.gender).toUpperCase();

        setInitial(p);
        setForm(p);
        setErrors({});
        setSavingField({});
        setSavedTick({});
        setEditingKey(null);
      } catch (err) {        // ✅ Ignore abort/cancel (e.g. React StrictMode cleanup)
        if (err?.isCanceled || err?.code === "ERR_CANCELED" || ctrl.signal.aborted) return;
        const msg = err?.message || "Couldn’t load profile. Your session may have expired.";

        const base = swalBaseRef.current;
        const expired = onAuthExpiredRef.current;

        if (base) {
          const res = await Swal.fire({
            ...base,
            icon: "error",
            title: "PROFILE UNAVAILABLE",
            html: msg,
            showCancelButton: true,
            confirmButtonText: "GO TO LOGIN",
            cancelButtonText: "CANCEL",
          });

          if (res.isConfirmed) expired?.();
        } else {
          expired?.();
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, []);

  const setField = useCallback((key, value) => {
    const cfg = FIELD_CONFIG[key] || {};
    let v = String(value ?? "");

    if (key === "postalCode") v = v.replace(/[^\d]/g, "");
    if (key === "phone") {
      v = v.replace(/[\s-]/g, "");
      v = v.replace(/[^0-9+]/g, "");
      if (v.includes("+")) {
        v = v[0] === "+" ? "+" + v.slice(1).replace(/\+/g, "") : v.replace(/\+/g, "");
      }
    }
    if (key === "birthdate") {
      // Preserve date format YYYY-MM-DD
      v = v.trim();
    }
    if (key === "gender") {
      v = v.toUpperCase();
    }

    if (cfg.maxLen) v = v.slice(0, cfg.maxLen);

    setForm((p) => ({ ...p, [key]: v }));

    const prepared = key === "phone" ? normalizePhone(v) : key === "birthdate" ? normalizeBirthdate(v) : clean(v);
    const err = validateField(key, prepared);
    setErrors((e) => ({ ...e, [key]: err || "" }));
  }, []);

  const beginEdit = useCallback(
    async (key) => {
      if (editingKey && editingKey !== key) {
        await toastRef.current?.fire?.({ icon: "info", title: "SAVE OR CANCEL CURRENT FIELD" });
        return;
      }
      // Sensitive fields always start with an empty input to prevent shoulder-surfing.
      // The masked value is shown as helper text below the input.
      if (isSensitive(key)) {
        setForm((p) => ({ ...p, [key]: "" }));
        setErrors((e) => ({ ...e, [key]: "" }));
      }
      setEditingKey(key);
    },
    [editingKey]
  );

  const cancelEdit = useCallback(
    (key) => {
      const initialVal =
        key === "phone" ? normalizePhone(initial?.[key] ?? "") : key === "birthdate" ? normalizeBirthdate(initial?.[key] ?? "") : clean(initial?.[key] ?? "");

      setForm((p) => ({ ...p, [key]: initialVal }));
      setErrors((e) => ({ ...e, [key]: "" }));
      setEditingKey(null);
    },
    [initial]
  );

  const saveField = useCallback(
    async (key) => {
      if (savingField[key]) return;

      const raw = form?.[key] ?? "";
      let prepared = key === "phone" ? normalizePhone(raw) : key === "birthdate" ? normalizeBirthdate(raw) : clean(raw);
      if (key === "gender") prepared = String(prepared).toUpperCase();

      // For sensitive fields: if the user left the input blank, treat as
      // "no change" and simply exit edit mode without calling the API.
      // This prevents accidentally clearing server-side values.
      if (isSensitive(key) && (!prepared || prepared === "")) {
        setEditingKey(null);
        setForm((p) => ({ ...p, [key]: initial?.[key] ?? "" }));
        return;
      }

      const initialVal =
        key === "phone" ? normalizePhone(initial?.[key] ?? "") : key === "birthdate" ? normalizeBirthdate(initial?.[key] ?? "") : clean(initial?.[key] ?? "");

      // unchanged -> exit edit mode
      if ((prepared ?? "") === (initialVal ?? "")) {
        setEditingKey(null);
        return;
      }

      const err = validateField(key, prepared);
      if (err) {
        setErrors((e) => ({ ...e, [key]: err }));
        await toastRef.current?.fire?.({ icon: "warning", title: "CHECK FIELD" });
        return;
      }

      const patch = { [key]: prepared };

      setSavingField((s) => ({ ...s, [key]: true }));

      try {
        const res = await userAPI.updateMe(patch);
        // if API returned full profile data, merge it instead of only patch
        if (res && typeof res === "object") {
          const updated = pickProfileFromMe(res);
          if (updated) {
            setInitial((p) => ({ ...p, ...updated }));
            setForm((p) => ({ ...p, ...updated }));
          } else {
            setInitial((p) => ({ ...p, ...patch }));
            setForm((p) => ({ ...p, ...patch }));
          }
        } else {
          setInitial((p) => ({ ...p, ...patch }));
          setForm((p) => ({ ...p, ...patch }));
        }
        setErrors((e) => ({ ...e, [key]: "" }));
        setSavedTick((t) => ({ ...t, [key]: Date.now() }));

        setEditingKey(null);
        await toastRef.current?.fire?.({ icon: "success", title: "SAVED" });
      } catch (e) {
        await toastRef.current?.fire?.({ icon: "error", title: "SAVE FAILED" });
        setErrors((prev) => ({
          ...prev,
          [key]: e?.message || "Save failed. Please try again.",
        }));
      } finally {
        setSavingField((s) => ({ ...s, [key]: false }));
      }
    },
    [savingField, form, initial]
  );

  const statusHelper = useCallback(
    (key, baseHelper = "") => {
      if (savingField[key]) return `${baseHelper ? baseHelper + " • " : ""}Saving…`;
      if (savedTick[key]) return `${baseHelper ? baseHelper + " • " : ""}Saved`;
      return baseHelper;
    },
    [savingField, savedTick]
  );

  const onKeyDownMaybeSave = useCallback(
    (key, e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        saveField(key);
        e.currentTarget?.blur?.();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        cancelEdit(key);
        e.currentTarget?.blur?.();
      }
    },
    [saveField, cancelEdit]
  );

  return {
    loading,
    initial,
    form,
    errors,
    editingKey,
    savingField,
    savedTick,
    setField,
    beginEdit,
    cancelEdit,
    saveField,
    statusHelper,
    onKeyDownMaybeSave,

    // expose setters for avatar updates
    setForm,
    setInitial,
  };
}
